import { ECouponType, ECouponStatus, ECouponApplyOn, ECouponApplyOnItemsConstraints } from '@common/enums/coupon.enum';
import { t } from '@common/hooks/use-t.hook';
import { IBEError } from '@common/interfaces/be-error.interface';
import { TItemV1CouponCodeIdGetError } from '@src/api/item-generated-api';
import { ICachedItemPriceDto } from '@src/api/model/cached-item-price.dto';
import { ICouponDto } from '@src/api/model/coupon.dto';
import { IProductCatalogItemDto } from '@src/api/model/product-catalog-item.dto';
import { IDeliveryCost } from '@src/api/pack-house-generated-api';
import { ReactNode } from 'react';

export interface ICalcItem {
    quantity: number;
    price: ICachedItemPriceDto;
    product: IProductCatalogItemDto;
}

interface IProps {
    items: ICalcItem[];
    shippingPrice?: IDeliveryCost;
    couponId?: string;
    coupon?: ICouponDto;
    couponError: IBEError<TItemV1CouponCodeIdGetError> | null;
}

export interface ICalcPriceData {
    subTotalPrice: number;
    totalPrice: number;
    discountInfo: string;
    discountTitle: string;
    error?: ECalculatePriceErrors;
    disableCouponFieldFor?: number;
    shippingPrice: number;
}

export enum ECalculatePriceErrors {
    CouponNotApplicable = 'CouponNotApplicable',
    CouponNotFound = 'CouponNotFound',
    maxAttemptsExceeded = 'MaxAttemptsExceeded',
    promoCodeAlreadyRedeemed = 'PromoCodeAlreadyRedeemed',
    CouponExpired = 'CouponExpired',
    specificConstrainForPlanNotValid = 'specificConstrainForPlanNotValid',
}

const FRIENDLY_ERROR_MESSAGES: Record<ECalculatePriceErrors, ReactNode> = {
    [ECalculatePriceErrors.CouponExpired]: t(
        "Coupon's validity time period is expired. Please, contact our customer services team for assistance",
    ),
    [ECalculatePriceErrors.CouponNotApplicable]: t(
        'Coupon can be applied to "Specified items" which are not in the current Order. Please, contact our customer services team for assistance',
    ),
    [ECalculatePriceErrors.CouponNotFound]: t(
        "Coupon doesn't exist. Please, contact our customer services team for assistance",
    ),
    [ECalculatePriceErrors.specificConstrainForPlanNotValid]: t('Specific constrain for plan not valid'),
    [ECalculatePriceErrors.promoCodeAlreadyRedeemed]: t(
        'This promo code is already redeemed. Please try again with another promo code',
    ),
    [ECalculatePriceErrors.maxAttemptsExceeded]: t('Hmm, that code doesn’t look right.  Please check and try again.'),
};

export const getCouponFriendlyErrorMessage = (code: ECalculatePriceErrors): ReactNode => FRIENDLY_ERROR_MESSAGES[code];

export const calculatePrice = ({ items, coupon, couponError, couponId, shippingPrice }: IProps): ICalcPriceData => {
    const [first] = items;
    return calculatePriceItem({ first, coupon, couponError, couponId, shippingPrice });
};

interface IItemProps {
    first: ICalcItem;
    couponId?: string;
    coupon?: ICouponDto;
    couponError: IBEError<TItemV1CouponCodeIdGetError> | null;
    shippingPrice?: IDeliveryCost;
}

export const calculatePriceItem = ({ first, coupon, couponError, couponId, shippingPrice }: IItemProps): ICalcPriceData => {
    const { quantity, price } = first;
    const subTotalPrice = (price.price || 0) * quantity;
    const totalPrice = subTotalPrice + (parseInt(shippingPrice?.every_other_week as unknown as string, 10) || 0);
    const discountInfo = '';
    const discountTitle = '';
    if (coupon && coupon?.status !== ECouponStatus.active) {
        return {
            error: ECalculatePriceErrors.CouponExpired,
            discountInfo,
            discountTitle,
            subTotalPrice,
            totalPrice,
            shippingPrice: shippingPrice?.every_week || 0,
        };
    } else if (couponId && !coupon) {
        const disableCouponFieldFor =
            (couponError?.error as string) === ECalculatePriceErrors.maxAttemptsExceeded
                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (couponError as any)?.remaining_time
                : undefined;

        return {
            error: (couponError?.error as ECalculatePriceErrors) || ECalculatePriceErrors.CouponNotFound,
            disableCouponFieldFor,
            discountInfo,
            discountTitle,
            subTotalPrice,
            totalPrice,
            shippingPrice: shippingPrice?.every_week || 0,
        };
    }
    if (coupon && couponId) {
        //calculate discount
        let result = subTotalPrice;
        if (coupon.apply_on === ECouponApplyOn.each_specified_item) {
            coupon?.item_constraints?.forEach((itemConstraint) => {
                const isPlanType = itemConstraint.item_type === ECouponApplyOnItemsConstraints.plan;
                const isAllConstraint = itemConstraint.constraint === 'all';
                const isSpecificConstraint = itemConstraint.constraint === 'specific';
                const isSelectedBoxInConstraint = itemConstraint.item_price_ids?.includes(price.id);

                if (isPlanType && (isAllConstraint || (isSpecificConstraint && isSelectedBoxInConstraint))) {
                    if (coupon.discount_type === ECouponType.fixed_amount) {
                        result = result - coupon.discount_amount;
                    } else {
                        result = result - (result * coupon.discount_percentage) / 100;
                    }
                }
            });

            result += parseInt(shippingPrice?.every_other_week as unknown as string, 10) || 0;
            const isCouponNotApplicable = totalPrice === result && coupon.apply_on === ECouponApplyOn.each_specified_item;

            return {
                error: isCouponNotApplicable ? ECalculatePriceErrors.CouponNotApplicable : undefined,
                discountTitle: isCouponNotApplicable ? '' : coupon.title,
                discountInfo: isCouponNotApplicable ? '' : coupon.label,
                subTotalPrice,
                totalPrice: result,
                shippingPrice: shippingPrice?.every_week || 0,
            };
        } else {
            result =
                coupon.discount_type === ECouponType.fixed_amount
                    ? totalPrice - coupon.discount_amount
                    : totalPrice - (totalPrice * coupon.discount_percentage) / 100;

            const isCouponNotApplicable = totalPrice === result && coupon.apply_on === ECouponApplyOn.invoice_amount;

            return {
                error: isCouponNotApplicable ? ECalculatePriceErrors.CouponNotApplicable : undefined,
                discountTitle: isCouponNotApplicable ? '' : coupon.title,
                discountInfo: isCouponNotApplicable ? '' : coupon.label,
                subTotalPrice,
                totalPrice: result,
                shippingPrice: shippingPrice?.every_week || 0,
            };
        }
    }

    return {
        discountInfo,
        discountTitle,
        subTotalPrice,
        totalPrice,
        shippingPrice: shippingPrice?.every_week || 0,
    };
};
